/**
 * Test Data for UnifiedDataGrid Prototype
 *
 * Mock data that simulates the structure of real organization data
 * for testing inline and mass update functionality.
 */

import { SelectOption } from '../types/EntityConfig'

/**
 * Mock users
 */
export const MOCK_USERS = [
  { user_uuid: 'u1', name: 'John Smith', email: 'john@example.com', image_512: 'https://i.pravatar.cc/150?u=john' },
  { user_uuid: 'u2', name: 'Jane Doe', email: 'jane@example.com', image_512: 'https://i.pravatar.cc/150?u=jane' },
  { user_uuid: 'u3', name: 'Bob Wilson', email: 'bob@example.com', image_512: 'https://i.pravatar.cc/150?u=bob' },
  { user_uuid: 'u4', name: 'Alice Brown', email: 'alice@example.com', image_512: 'https://i.pravatar.cc/150?u=alice' },
  { user_uuid: 'u5', name: 'Charlie Davis', email: 'charlie@example.com', image_512: 'https://i.pravatar.cc/150?u=charlie' },
  { user_uuid: 'u6', name: 'Diana Evans', email: 'diana@example.com', image_512: 'https://i.pravatar.cc/150?u=diana' },
  { user_uuid: 'u7', name: 'Edward Foster', email: 'edward@example.com', image_512: 'https://i.pravatar.cc/150?u=edward' },
  { user_uuid: 'u8', name: 'Fiona Green', email: 'fiona@example.com', image_512: 'https://i.pravatar.cc/150?u=fiona' },
]

/**
 * Mock contacts
 */
export const MOCK_CONTACTS = [
  { contacts_uuid: 'c1', name: 'Contact One', email: 'contact1@example.com', phone: '555-0101' },
  { contacts_uuid: 'c2', name: 'Contact Two', email: 'contact2@example.com', phone: '555-0102' },
  { contacts_uuid: 'c3', name: 'Contact Three', email: 'contact3@example.com', phone: '555-0103' },
  { contacts_uuid: 'c4', name: 'Contact Four', email: 'contact4@example.com', phone: '555-0104' },
  { contacts_uuid: 'c5', name: 'Contact Five', email: 'contact5@example.com', phone: '555-0105' },
  { contacts_uuid: 'c6', name: 'Contact Six', email: 'contact6@example.com', phone: '555-0106' },
]

/**
 * Status options
 */
export const STATUS_OPTIONS: SelectOption[] = [
  { label: 'Active', value: 'active', color: '#4caf50' },
  { label: 'Inactive', value: 'inactive', color: '#9e9e9e' },
  { label: 'Pending', value: 'pending', color: '#ff9800' },
  { label: 'On Hold', value: 'on_hold', color: '#f44336' },
]

/**
 * Type options
 */
export const TYPE_OPTIONS: SelectOption[] = [
  { label: 'Client', value: 'client' },
  { label: 'Partner', value: 'partner' },
  { label: 'Prospect', value: 'prospect' },
  { label: 'Vendor', value: 'vendor' },
]

/**
 * Vertical options
 */
export const VERTICAL_OPTIONS: SelectOption[] = [
  { label: 'Technology', value: 'technology' },
  { label: 'Healthcare', value: 'healthcare' },
  { label: 'Finance', value: 'finance' },
  { label: 'Retail', value: 'retail' },
  { label: 'Manufacturing', value: 'manufacturing' },
  { label: 'Education', value: 'education' },
]

/**
 * Mock organization rows
 */
export interface MockRow {
  id: string
  rowId: string // Required by GlobalDataGrid's getRowId
  PK: string
  SK: string
  org_uuid: string
  name: string
  website: string
  status: string
  type: string
  vertical: string
  owner: string[]
  primaryContact: string[]
  team: {
    AM: string[]
    PM: string[]
  }
  _resolvedTeam: {
    AM: typeof MOCK_USERS
    PM: typeof MOCK_USERS
  }
  contractedBy: string[]
  createdAt: string
  monthlySpend: number
}

export const MOCK_ROWS: MockRow[] = [
  {
    id: '1',
    rowId: '1',
    PK: 'ORG#1',
    SK: 'META',
    org_uuid: '1',
    name: 'Acme Corporation',
    website: 'https://acme.example.com',
    status: 'active',
    type: 'client',
    vertical: 'technology',
    owner: ['u1'],
    primaryContact: ['c1'],
    team: { AM: ['u1', 'u2'], PM: ['u3'] },
    _resolvedTeam: {
      AM: [MOCK_USERS[0], MOCK_USERS[1]],
      PM: [MOCK_USERS[2]],
    },
    contractedBy: [],
    createdAt: '2024-01-15',
    monthlySpend: 15000,
  },
  {
    id: '2',
    rowId: '2',
    PK: 'ORG#2',
    SK: 'META',
    org_uuid: '2',
    name: 'TechStart Inc',
    website: 'https://techstart.example.com',
    status: 'active',
    type: 'partner',
    vertical: 'technology',
    owner: ['u2'],
    primaryContact: ['c2'],
    team: { AM: ['u4'], PM: ['u5'] },
    _resolvedTeam: {
      AM: [MOCK_USERS[3]],
      PM: [MOCK_USERS[4]],
    },
    contractedBy: ['1'],
    createdAt: '2024-02-20',
    monthlySpend: 8500,
  },
  {
    id: '3',
    rowId: '3',
    PK: 'ORG#3',
    SK: 'META',
    org_uuid: '3',
    name: 'Global Health Partners',
    website: 'https://ghp.example.com',
    status: 'pending',
    type: 'prospect',
    vertical: 'healthcare',
    owner: ['u3'],
    primaryContact: ['c3'],
    team: { AM: ['u6'], PM: [] },
    _resolvedTeam: {
      AM: [MOCK_USERS[5]],
      PM: [],
    },
    contractedBy: [],
    createdAt: '2024-03-10',
    monthlySpend: 0,
  },
  {
    id: '4',
    rowId: '4',
    PK: 'ORG#4',
    SK: 'META',
    org_uuid: '4',
    name: 'Finance First LLC',
    website: 'https://financefirst.example.com',
    status: 'active',
    type: 'client',
    vertical: 'finance',
    owner: ['u1'],
    primaryContact: ['c4'],
    team: { AM: ['u2', 'u7'], PM: ['u8'] },
    _resolvedTeam: {
      AM: [MOCK_USERS[1], MOCK_USERS[6]],
      PM: [MOCK_USERS[7]],
    },
    contractedBy: [],
    createdAt: '2024-01-05',
    monthlySpend: 25000,
  },
  {
    id: '5',
    rowId: '5',
    PK: 'ORG#5',
    SK: 'META',
    org_uuid: '5',
    name: 'Retail Revolution',
    website: 'https://retailrev.example.com',
    status: 'inactive',
    type: 'client',
    vertical: 'retail',
    owner: ['u4'],
    primaryContact: ['c5'],
    team: { AM: ['u3'], PM: ['u1'] },
    _resolvedTeam: {
      AM: [MOCK_USERS[2]],
      PM: [MOCK_USERS[0]],
    },
    contractedBy: ['1', '4'],
    createdAt: '2023-11-20',
    monthlySpend: 5000,
  },
  {
    id: '6',
    rowId: '6',
    PK: 'ORG#6',
    SK: 'META',
    org_uuid: '6',
    name: 'EduTech Solutions',
    website: 'https://edutech.example.com',
    status: 'on_hold',
    type: 'partner',
    vertical: 'education',
    owner: ['u5'],
    primaryContact: ['c6'],
    team: { AM: ['u6', 'u7'], PM: ['u8'] },
    _resolvedTeam: {
      AM: [MOCK_USERS[5], MOCK_USERS[6]],
      PM: [MOCK_USERS[7]],
    },
    contractedBy: ['2'],
    createdAt: '2024-04-01',
    monthlySpend: 12000,
  },
  {
    id: '7',
    rowId: '7',
    PK: 'ORG#7',
    SK: 'META',
    org_uuid: '7',
    name: 'Manufacturing Masters',
    website: 'https://mfgmasters.example.com',
    status: 'active',
    type: 'client',
    vertical: 'manufacturing',
    owner: ['u6'],
    primaryContact: ['c1'],
    team: { AM: ['u1'], PM: ['u2', 'u3'] },
    _resolvedTeam: {
      AM: [MOCK_USERS[0]],
      PM: [MOCK_USERS[1], MOCK_USERS[2]],
    },
    contractedBy: [],
    createdAt: '2024-02-28',
    monthlySpend: 18000,
  },
  {
    id: '8',
    rowId: '8',
    PK: 'ORG#8',
    SK: 'META',
    org_uuid: '8',
    name: 'CloudScale Systems',
    website: 'https://cloudscale.example.com',
    status: 'active',
    type: 'vendor',
    vertical: 'technology',
    owner: ['u7'],
    primaryContact: ['c2'],
    team: { AM: ['u4', 'u5'], PM: ['u6'] },
    _resolvedTeam: {
      AM: [MOCK_USERS[3], MOCK_USERS[4]],
      PM: [MOCK_USERS[5]],
    },
    contractedBy: ['1', '2', '4'],
    createdAt: '2024-03-15',
    monthlySpend: 22000,
  },
  {
    id: '9',
    rowId: '9',
    PK: 'ORG#9',
    SK: 'META',
    org_uuid: '9',
    name: 'HealthCare Dynamics',
    website: 'https://hcdynamics.example.com',
    status: 'pending',
    type: 'prospect',
    vertical: 'healthcare',
    owner: ['u8'],
    primaryContact: ['c3'],
    team: { AM: [], PM: [] },
    _resolvedTeam: {
      AM: [],
      PM: [],
    },
    contractedBy: [],
    createdAt: '2024-05-01',
    monthlySpend: 0,
  },
  {
    id: '10',
    rowId: '10',
    PK: 'ORG#10',
    SK: 'META',
    org_uuid: '10',
    name: 'Financial Frontiers',
    website: 'https://finfrontiers.example.com',
    status: 'active',
    type: 'client',
    vertical: 'finance',
    owner: ['u1'],
    primaryContact: ['c4'],
    team: { AM: ['u2'], PM: ['u3', 'u4'] },
    _resolvedTeam: {
      AM: [MOCK_USERS[1]],
      PM: [MOCK_USERS[2], MOCK_USERS[3]],
    },
    contractedBy: ['4'],
    createdAt: '2024-01-25',
    monthlySpend: 30000,
  },
]

/**
 * Mock organizations (for org reference lookups)
 */
export const MOCK_ORGANIZATIONS = MOCK_ROWS.map(row => ({
  org_uuid: row.org_uuid,
  name: row.name,
  PK: row.PK,
  SK: row.SK,
}))

/**
 * All options combined for easy access
 */
export const ALL_OPTIONS = {
  statusOptions: STATUS_OPTIONS,
  typeOptions: TYPE_OPTIONS,
  verticalOptions: VERTICAL_OPTIONS,
  users: MOCK_USERS,
  contacts: MOCK_CONTACTS,
  organizations: MOCK_ORGANIZATIONS,
}
